import { Fragment } from 'react';
import { type RequestedDataGroup } from '../../hooks/useRequestedData';

type Column = {
  header: string;
  render: (field: RequestedDataGroup['fields'][number]) => React.ReactNode;
  className?: string;
};

type Props = {
  group: RequestedDataGroup;
  columns: [Column, Column];
};

/**
 * HostedDataTable — Two-column grid for credential attribute data.
 *
 * Uses CSS Grid with `minmax(0, fr)` for proportional, overflow-safe columns.
 * The parent defines what each column shows via the `columns` render prop.
 */
export default function HostedDataTable({ group, columns }: Props) {
  const [left, right] = columns;

  return (
    <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
      {/* Column headers */}
      <div className="flex h-10 items-center border-b border-border px-4">
        <span className="text-sm font-medium text-card-foreground">
          {left.header}
        </span>
      </div>
      <div className="flex h-10 items-center border-b border-border px-2">
        <span className="text-sm font-medium text-card-foreground">
          {right.header}
        </span>
      </div>
      {/* Rows */}
      {group.fields.map((field) => (
        <Fragment key={field.attributeUuid}>
          <div className="flex items-center px-4 py-3">
            <span
              className={`text-sm truncate ${left.className ?? 'text-foreground'}`}
            >
              {left.render(field)}
            </span>
          </div>
          <div className="flex items-center px-2 py-3">
            <span
              className={`text-sm ${right.className ?? 'text-muted-foreground'}`}
            >
              {right.render(field)}
            </span>
          </div>
        </Fragment>
      ))}
    </div>
  );
}
